<template>
  <div v-if="isButtonVisible">
    <v2-button
      variant="outline"
      :disabled="isDuplicating || !hasValidProducts"
      @click="showDuplicateConfirmModal"
    >
      <v2-spinner
        v-if="isDuplicating"
        size="sm"
        class="v2-mr-2"
      />
      {{ isDuplicating ? 'Duplicating...' : 'Duplicate Order' }}
    </v2-button>

    <!-- Same Collection Modal (Retailer only) -->
    <v2-modal
      :show="showSameCollectionModal"
      title="Add to Cart"
      @close="closeSameCollectionModal"
    >
      <p>Your cart already contains products from this collection.</p>
      <p>Would you like to keep existing products or clear the cart?</p>
      <template #footer>
        <v2-button
          variant="outline"
          :disabled="isDuplicating"
          @click="duplicateWithMerge"
        >
          Keep existing products
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="isDuplicating"
          @click="duplicateWithClear"
        >
          Clear cart
        </v2-button>
      </template>
    </v2-modal>

    <!-- Different Collection Modal (Retailer only) -->
    <v2-modal
      :show="showDifferentCollectionModal"
      title="Different Collection"
      @close="closeDifferentCollectionModal"
    >
      <p>Your cart contains products from a different collection ({{ cartCollectionName }}).</p>
      <p>To proceed, your existing cart will need to be cleared.</p>
      <template #footer>
        <v2-button
          variant="outline"
          @click="closeDifferentCollectionModal"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="destructive"
          :disabled="isDuplicating"
          @click="duplicateWithClear"
        >
          Clear cart and proceed
        </v2-button>
      </template>
    </v2-modal>

    <!-- Duplicate Order Confirmation Modal -->
    <v2-modal
      :show="showConfirmModal"
      title="Duplicate Order"
      @close="closeConfirmModal"
    >
      <p>This will remove all cancelled and removed styles from the duplicated order.</p>
      <p>Do you want to continue?</p>
      <template #footer>
        <v2-button
          variant="outline"
          @click="closeConfirmModal"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="isDuplicating"
          @click="confirmDuplicateOrder"
        >
          Continue
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import V2Button from '@/components/v2/V2Button.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import {
  ADD_PRODUCT_TO_CART,
  FETCH_CART_PRODUCTS,
  FETCH_CART_COUNT,
  SET_DUPLICATE_ORDER_DATA,
  UPDATE_SELECTED_DIRECT_RETAILER,
  GET_PRODUCTS_BY_IDS,
} from '@/store/modules/shop.module'
import { FETCH_DIRECT_RETAILERS } from '@/store/modules/connections.module'
import constants from '@/constants'
import analytics from '@/@core/utils/analytics'
import { formatObject } from '@/@core/utils/utils'
import { mapState } from 'vuex'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'

const { ORDER_STATUS, ORDER_PRODUCT_SHIPMENT_STATUS, TRACKS } = constants

export default {
  name: 'V2DuplicateOrderButton',
  components: {
    V2Button,
    V2Modal,
    V2Spinner,
  },
  mixins: [UserRoleMixin],
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isDuplicating: false,
      cartCollectionId: null,
      cartCollectionName: null,
      retailerValidatedProducts: [],
      showSameCollectionModal: false,
      showDifferentCollectionModal: false,
      showConfirmModal: false,
    }
  },
  computed: {
    ...mapState({
      directRetailers: state => state.connections.directRetailers || [],
    }),
    isButtonVisible() {
      return this.order.status !== ORDER_STATUS.CANCELLED
    },
    validProducts() {
      return (this.order.products || []).filter(
        product => product.status !== ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL,
      )
    },
    hasValidProducts() {
      return this.validProducts.length > 0
    },
    hasExcludedProducts() {
      const totalProducts = this.order.products?.length || 0
      const validCount = this.validProducts.length
      return totalProducts > validCount
    },
  },
  methods: {
    showDuplicateConfirmModal() {
      if (!this.hasValidProducts) {
        apiToastWarning('No valid products to duplicate')
        return
      }
      if (this.hasExcludedProducts) {
        this.showConfirmModal = true
      } else {
        this.handleDuplicateOrder()
      }
    },
    closeConfirmModal() {
      this.showConfirmModal = false
    },
    confirmDuplicateOrder() {
      this.showConfirmModal = false
      this.handleDuplicateOrder()
    },
    async handleDuplicateOrder() {
      this.trackDuplicateOrderClick()
      if (this.isBrand) {
        this.handleBrandDuplicateOrder()
      } else {
        this.handleRetailerDuplicateOrder()
      }
    },
    async handleBrandDuplicateOrder() {
      this.isDuplicating = true
      try {
        let retailers = this.directRetailers
        if (!retailers.length) {
          retailers = await this.$store.dispatch(FETCH_DIRECT_RETAILERS)
        }
        const matchedRetailer = retailers.find(
          r => r.retailerId === this.order.retailerId,
        )
        const retailerData = {
          retailerId: this.order.retailerId,
          entityName: this.order.retailerName,
          email: matchedRetailer?.email || '',
          discount: this.order.discount ?? 1,
        }
        this.$store.commit(SET_DUPLICATE_ORDER_DATA, {
          products: this.validProducts,
          retailer: retailerData,
          orderId: this.order.orderSeqId,
          collectionId: this.order.collectionId,
        })
        this.$store.commit(UPDATE_SELECTED_DIRECT_RETAILER, retailerData)
        this.$router.push({
          name: 'v2-collection/products',
          params: { collectionId: this.order.collectionId },
          query: { duplicateOrder: 'true' },
        })
      } catch (error) {
        // error toast handled by axios interceptor
      } finally {
        this.isDuplicating = false
      }
    },
    async validateProductsVisibility() {
      const productIds = this.validProducts.map(p => p.productId || p._id)
      if (!productIds.length) return false
      try {
        const res = await this.$store.dispatch(GET_PRODUCTS_BY_IDS, {
          payload: { productIds },
        })
        this.filterVisibleProducts(res?.data?.data || [])
        return this.retailerValidatedProducts.length > 0
      } catch (error) {
        apiToastWarning('Failed to validate products')
        return false
      }
    },
    filterVisibleProducts(visibleProducts) {
      const visibleProductIds = visibleProducts.map(p => p._id)
      this.retailerValidatedProducts = this.validProducts.filter(
        p => visibleProductIds.includes(p.productId || p._id),
      )
      if (this.retailerValidatedProducts.length < this.validProducts.length) {
        apiToastWarning('Some products were removed as they are no longer available')
      }
    },
    async handleRetailerDuplicateOrder() {
      this.isDuplicating = true
      try {
        const hasValid = await this.validateProductsVisibility()
        if (!hasValid) {
          apiToastWarning('No products available to duplicate')
          this.isDuplicating = false
          return
        }
        await this.processRetailerCart()
      } catch (error) {
        this.isDuplicating = false
      }
    },
    async processRetailerCart() {
      const cartResponse = await this.$store.dispatch(FETCH_CART_PRODUCTS)
      const cartData = cartResponse?.data?.data
      const cart = Array.isArray(cartData) ? cartData[0] : cartData
      const isCartEmpty = !cart?.products?.length
      if (isCartEmpty) {
        await this.addProductsToCart(false)
        return
      }
      this.handleCartWithProducts(cart)
    },
    handleCartWithProducts(cart) {
      this.cartCollectionId = cart.collectionId
      this.cartCollectionName = cart.collectionName || 'another collection'
      this.isDuplicating = false
      const isSameCollection = cart.collectionId === this.order.collectionId
      if (isSameCollection) {
        this.showSameCollectionModal = true
      } else {
        this.showDifferentCollectionModal = true
      }
    },
    async addProductsToCart(shouldClearCart = false) {
      this.isDuplicating = true
      this.closeAllModals()
      try {
        const products = this.transformOrderProductsForCart()
        const params = shouldClearCart ? { shouldClearCart: true } : {}
        await this.$store.dispatch(ADD_PRODUCT_TO_CART, {
          payload: { products },
          params,
        })
        await this.$store.dispatch(FETCH_CART_COUNT)
        apiToastSuccess('Products added to cart!')
        this.trackDuplicateOrderSuccess()
        this.$router.push({ name: 'checkout' })
      } catch (error) {
        this.handleAddToCartError(error)
      } finally {
        this.isDuplicating = false
      }
    },
    handleAddToCartError(error) {
      if (error.response?.status === 425) {
        this.cartCollectionName = error.response?.data?.data?.collectionName || 'another collection'
        this.showDifferentCollectionModal = true
      }
    },
    async duplicateWithMerge() {
      await this.addProductsToCart(false)
    },
    async duplicateWithClear() {
      await this.addProductsToCart(true)
    },
    transformOrderProductsForCart() {
      const productsToTransform = this.retailerValidatedProducts.length > 0
        ? this.retailerValidatedProducts
        : this.validProducts
      return productsToTransform
        .map(product => {
          if (product.isCarton) {
            return {
              productId: product.productId,
              quantity: product.quantity,
            }
          }
          return {
            productId: product.productId,
            items: (product.items || [])
              .filter(item => item.value > 0)
              .map(item => ({
                sku: item.sku || item.skuId,
                value: item.value,
              })),
          }
        })
        .filter(product => {
          if (product.quantity !== undefined) {
            return product.quantity > 0
          }
          return product.items && product.items.length > 0
        })
    },
    closeSameCollectionModal() {
      this.showSameCollectionModal = false
      this.isDuplicating = false
    },
    closeDifferentCollectionModal() {
      this.showDifferentCollectionModal = false
      this.isDuplicating = false
    },
    closeAllModals() {
      this.showSameCollectionModal = false
      this.showDifferentCollectionModal = false
      this.showConfirmModal = false
    },
    trackDuplicateOrderClick() {
      const trackName = this.isBrand
        ? TRACKS.ACTIONS.SINGLE_ORDER.BRAND_CLICKS_DUPLICATE_ORDER
        : TRACKS.ACTIONS.SINGLE_ORDER.RETAILER_CLICKS_DUPLICATE_ORDER
      analytics.track(
        trackName,
        formatObject({
          orderId: this.order.orderSeqId,
          collectionId: this.order.collectionId,
          collectionName: this.order.collectionName,
          productCount: this.validProducts.length,
        }),
      )
    },
    trackDuplicateOrderSuccess() {
      const trackName = this.isBrand
        ? TRACKS.ACTIONS.SINGLE_ORDER.BRAND_DUPLICATE_ORDER_SUCCESS
        : TRACKS.ACTIONS.SINGLE_ORDER.RETAILER_DUPLICATE_ORDER_SUCCESS
      const productCount = !this.isBrand && this.retailerValidatedProducts.length > 0
        ? this.retailerValidatedProducts.length
        : this.validProducts.length
      analytics.track(
        trackName,
        formatObject({
          orderId: this.order.orderSeqId,
          collectionId: this.order.collectionId,
          productCount,
        }),
      )
    },
  },
}
</script>
